import { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { FadeIn } from '../../../components/animations';
import type { ColoredDataPoint, DataPoint } from '../../../components/charts';
import { ResponsiveDonutChart, ResponsiveLineChart } from '../../../components/charts/responsive';
import { Box, VStack } from '../../../components/primitives';
import { useSourceLabel } from '../../../hooks/useSourceLabel';
import { colors } from '../../../theme/colors';
import { ChartCard } from '../../analytics/components/ChartCard';
import type { DashboardSummaryResponse } from '../../analytics/hooks/useAnalyticsData';

interface DashboardChartsProps {
  summary: DashboardSummaryResponse;
}

// FLOSEAI-438: charts sit side-by-side only on wide desktop (AC1, ≥1280px);
// below that they stack and each fills the full width (AC2 tablet, AC3 mobile).
const SIDE_BY_SIDE_BREAKPOINT = 1280;

const SOURCE_COLORS: Record<string, string> = {
  email: colors.primary.neon,
  linkedin: colors.accent.purple.neon,
  web_form: colors.accent.blue.neon,
  scraping: colors.accent.amber.neon,
  linkedin_enrichment: colors.accent.blue[400],
  import: colors.primary[200],
  manual: colors.primary[400],
  api: colors.status.info.main,
  referral: colors.status.warning.main,
  other: colors.text.muted,
};

const FALLBACK_COLORS = [
  colors.accent.amber.neon,
  colors.status.info.main,
  colors.accent.cyan.neon,
  colors.status.warning.main,
  colors.accent.purple.neon,
];

function getSourceColor(source: string, index: number): string {
  const normalized = source.toLowerCase().replaceAll(' ', '_');
  return SOURCE_COLORS[normalized] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

export function DashboardCharts({ summary }: Readonly<DashboardChartsProps>) {
  const sourceLabel = useSourceLabel();
  const { width } = useWindowDimensions();
  const isSideBySide = width >= SIDE_BY_SIDE_BREAKPOINT;

  const leadsChartData: DataPoint[] = useMemo(() => {
    return (summary.leadsTrend ?? []).map((point) => ({
      label: point.date.slice(5),
      value: point.count,
    }));
  }, [summary.leadsTrend]);

  const sourceChartData: ColoredDataPoint[] = useMemo(() => {
    return (summary.sourceDistribution ?? [])
      .filter((s) => s.count > 0)
      .map((s, i) => ({
        label: sourceLabel(s.source),
        value: s.count,
        color: getSourceColor(s.source, i),
      }));
  }, [summary.sourceDistribution, sourceLabel]);

  const hasLeadsData = leadsChartData.length >= 2;
  const hasSourceData = sourceChartData.length > 0;

  if (!hasLeadsData && !hasSourceData) {
    return null;
  }

  const totalSourceLeads = sourceChartData.reduce((sum, s) => sum + s.value, 0);

  const chartBoxStyle = isSideBySide ? { flex: 1, flexBasis: 0 } : undefined;

  return (
    <FadeIn delay={400} translateY={15}>
      <Box flexDirection={isSideBySide ? 'row' : 'column'} gap="lg" alignItems="stretch">
        {hasLeadsData && (
          <Box style={chartBoxStyle}>
            <ChartCard
              title="Leads Over Time"
              subtitle={`${summary.kpis?.totalLeads ?? 0} leads this period`}
              accentColor="cyan"
              delay={400}
            >
              <ResponsiveLineChart data={leadsChartData} showYAxisLabels={false} />
            </ChartCard>
          </Box>
        )}

        {hasSourceData && (
          <Box style={chartBoxStyle}>
            <ChartCard
              title="Lead Sources"
              subtitle={`${totalSourceLeads} leads by source`}
              accentColor="purple"
              delay={500}
            >
              <ResponsiveDonutChart data={sourceChartData} total={totalSourceLeads} />
            </ChartCard>
          </Box>
        )}
      </Box>
    </FadeIn>
  );
}

export function DashboardChartsLoading() {
  const { width } = useWindowDimensions();
  const isSideBySide = width >= SIDE_BY_SIDE_BREAKPOINT;

  return (
    <Box flexDirection={isSideBySide ? 'row' : 'column'} gap="lg" alignItems="stretch">
      {[0, 1].map((i) => (
        <Box key={i} style={{ flex: 1 }}>
          <Box
            backgroundColor={colors.background.card}
            borderRadius="2xl"
            borderWidth={1}
            borderColor={colors.border.muted}
            padding="xl"
            style={{ minHeight: 280 }}
          >
            <VStack spacing="lg">
              <VStack spacing="xs">
                <Box
                  backgroundColor={colors.surface.row}
                  borderRadius="md"
                  style={{ width: 120, height: 16 }}
                />
                <Box
                  backgroundColor={colors.surface.row}
                  borderRadius="md"
                  style={{ width: 180, height: 12 }}
                />
              </VStack>
              <Box
                backgroundColor={colors.surface.row}
                borderRadius="md"
                style={{ flex: 1, minHeight: 180 }}
              />
            </VStack>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
